<script setup lang="ts">
  import { Chip, ChipSize, Icon, IconName, IconSize } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import type { DestinationChipItem } from '../model/types';

  type DestinationMobileChipsSize = `${ChipSize.S}` | `${ChipSize.M}`;

  interface DestinationMobileChipsProps {
    items: DestinationChipItem[];
    size?: DestinationMobileChipsSize;
  }

  const props = withDefaults(defineProps<DestinationMobileChipsProps>(), {
    size: ChipSize.M,
  });

  const REMOVE_ICON_SIZES: Record<DestinationMobileChipsSize, `${IconSize}`> = {
    [ChipSize.S]: IconSize.S,
    [ChipSize.M]: IconSize.XS,
  };

  const removeIconSize = computed<`${IconSize}`>(() => REMOVE_ICON_SIZES[props.size]);

  interface DestinationMobileChipsEmits {
    (e: 'remove', key: string): void;
  }

  const emit = defineEmits<DestinationMobileChipsEmits>();
</script>

<template>
  <div
    :class="['destination-mobile-chips', `destination-mobile-chips_size-${size}`]"
    data-testid="destination-mobile-chips"
  >
    <Chip
      v-for="item in items"
      :key="item.key"
      class="destination-mobile-chips__chip"
      appearance="filled"
      rounded
      :size="size"
    >
      <span
        :class="['destination-mobile-chips__text', { 'destination-mobile-chips__text_clip': item.truncate }]"
        :title="item.truncate ? item.label : undefined"
      >
        {{ item.label }}
      </span>
      <template #append>
        <button
          v-if="item.removable"
          type="button"
          class="destination-mobile-chips__remove"
          data-testid="destination-mobile-chip-remove"
          aria-label="Убрать из выбора"
          @click.stop="emit('remove', item.key)"
        >
          <Icon
            :name="IconName.Close"
            :size="removeIconSize"
          />
        </button>
      </template>
    </Chip>
  </div>
</template>

<style scoped lang="scss">
  .destination-mobile-chips {
    display: flex;
    flex: 0 0 auto;
    gap: var(--spacing-xxs);
    overflow-x: auto;
    padding: var(--spacing-xxs) var(--spacing-m) var(--spacing-xs);
    scrollbar-width: none;

    &::-webkit-scrollbar {
      height: 0;
    }

    &_size-s {
      gap: var(--spacing-xs);
    }

    &__chip {
      flex: 0 0 auto;
    }

    &_size-s &__chip {
      gap: var(--spacing-xxs);
    }

    &__text_clip {
      display: block;
      max-width: 124px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__remove {
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      display: inline-flex;
      fill: var(--color-text-secondary);
      justify-content: center;
      padding: 0;
    }
  }
</style>
